import styled from 'styled-components';
import {graphql, ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import type {AnalyticsRouteQuery as AnalyticsRouteQueryType} from './__generated__/AnalyticsRouteQuery.graphql';

// ACC-298 (AC #6): internal-only AI summary feedback analytics. Admin-gated by
// <RequireAdmin> at the route and by guardQuery(TICKETS_ADMIN) at the GraphQL
// layer; account-scoped by the gateway. Mirrors SettingsRoute's
// ForecastQueryRenderer + presentational-child split.
const AnalyticsRouteQueryDoc = graphql`
	query AnalyticsRouteQuery {
		viewer {
			actualPersonId
			aiSummaryFeedbackAnalytics {
				totals {
					up
					down
					unrated
					total
				}
				byModelVersion {
					modelVersion
					up
					down
					unrated
					total
				}
				byStatus {
					status
					up
					down
					unrated
					total
				}
				overTime {
					date
					up
					down
					total
				}
				recentDownvotes {
					ticketId
					feedbackText
					modelVersion
					generatedAt
				}
			}
		}
	}
`;

type Analytics = NonNullable<NonNullable<AnalyticsRouteQueryType['response']['viewer']>['aiSummaryFeedbackAnalytics']>;

const Container = styled.div`
	margin: 0 auto;
	max-width: 64rem;
	padding: 24px;
`;
const Title = styled.h1`
	${p => p.theme.sys.font?.heading?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	margin: 0;
`;
const SectionTitle = styled.h2`
	${p => p.theme.sys.font?.subheading?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
	margin: 24px 0 8px;
`;
const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	${p => p.theme.sys.font?.body?.['2']}
	th,
	td {
		text-align: left;
		padding: 6px 8px;
		border-bottom: 1px solid ${p => p.theme.sys.color.border.decorative};
	}
`;
const Empty = styled.p`
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Loading = styled.div`
	padding: 16px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

export function AnalyticsContent({analytics}: {analytics: Analytics}) {
	const {totals, byModelVersion, byStatus, overTime, recentDownvotes} = analytics;
	return (
		<Container>
			<Title>AI summary feedback</Title>

			<SectionTitle>Totals</SectionTitle>
			<Table>
				<thead>
					<tr>
						<th>👍 Up</th>
						<th>👎 Down</th>
						<th>Unrated</th>
						<th>Total</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>{totals.up}</td>
						<td>{totals.down}</td>
						<td>{totals.unrated}</td>
						<td>{totals.total}</td>
					</tr>
				</tbody>
			</Table>

			<SectionTitle>By model version</SectionTitle>
			<Table>
				<thead>
					<tr>
						<th>Model</th>
						<th>👍</th>
						<th>👎</th>
						<th>Unrated</th>
						<th>Total</th>
					</tr>
				</thead>
				<tbody>
					{byModelVersion.map(r => (
						<tr key={r.modelVersion}>
							<td>{r.modelVersion}</td>
							<td>{r.up}</td>
							<td>{r.down}</td>
							<td>{r.unrated}</td>
							<td>{r.total}</td>
						</tr>
					))}
				</tbody>
			</Table>

			<SectionTitle>By ticket status</SectionTitle>
			<Table>
				<thead>
					<tr>
						<th>Status</th>
						<th>👍</th>
						<th>👎</th>
						<th>Unrated</th>
						<th>Total</th>
					</tr>
				</thead>
				<tbody>
					{byStatus.map(r => (
						<tr key={r.status}>
							<td>{r.status}</td>
							<td>{r.up}</td>
							<td>{r.down}</td>
							<td>{r.unrated}</td>
							<td>{r.total}</td>
						</tr>
					))}
				</tbody>
			</Table>

			<SectionTitle>Over time</SectionTitle>
			<Table>
				<thead>
					<tr>
						<th>Date</th>
						<th>👍</th>
						<th>👎</th>
						<th>Total</th>
					</tr>
				</thead>
				<tbody>
					{overTime.map(r => (
						<tr key={r.date}>
							<td>{r.date}</td>
							<td>{r.up}</td>
							<td>{r.down}</td>
							<td>{r.total}</td>
						</tr>
					))}
				</tbody>
			</Table>

			<SectionTitle>Recent downvote notes</SectionTitle>
			{recentDownvotes.length === 0 ? (
				<Empty>No downvote notes yet.</Empty>
			) : (
				<Table>
					<thead>
						<tr>
							<th>When</th>
							<th>Model</th>
							<th>Note</th>
						</tr>
					</thead>
					<tbody>
						{recentDownvotes.map(r => (
							<tr key={`${r.ticketId}-${r.generatedAt}`}>
								<td>{r.generatedAt}</td>
								<td>{r.modelVersion}</td>
								<td>{r.feedbackText}</td>
							</tr>
						))}
					</tbody>
				</Table>
			)}
		</Container>
	);
}

export function AnalyticsRoute() {
	return (
		<ForecastQueryRenderer
			query={AnalyticsRouteQueryDoc}
			variables={{}}
			customLoader={() => <Loading>Loading…</Loading>}
			redirectIfNotLoggedIn
			render={(relayProps: AnalyticsRouteQueryType['response'] | null) => {
				if (!relayProps || !relayProps.viewer || !relayProps.viewer.aiSummaryFeedbackAnalytics) {
					return null;
				}
				return <AnalyticsContent analytics={relayProps.viewer.aiSummaryFeedbackAnalytics} />;
			}}
		/>
	);
}
